/**
 * Comparaison ligne à ligne de deux versions de code (§3.5).
 *
 * Module volontairement pur — aucun accès au DOM, aucune dépendance React —
 * pour être testable directement sous Vitest. Le rendu (côte à côte ou unifié)
 * ne fait que peindre les lignes calculées ici.
 *
 * Le calcul s'appuie sur `diffLines` de la bibliothèque `diff` (algorithme de
 * Myers) : la comparaison naïve « cette ligne existe-t-elle dans l'autre
 * fichier ? » que faisait la première version du comparateur ne distinguait ni
 * les déplacements ni les insertions, et décalait toute la numérotation dès la
 * première ligne ajoutée.
 */

import { diffLines } from 'diff';

/** Nature d'une ligne dans le comparatif. */
export type DiffRowKind = 'added' | 'removed' | 'unchanged';

/**
 * Une ligne du comparatif, numérotée **des deux côtés**.
 *
 * Une ligne ajoutée n'existe pas dans la version de base : son numéro de base
 * vaut `null`, et réciproquement. C'est cette double numérotation qui permet
 * d'afficher les gouttières correctes en vue unifiée comme en vue côte à côte.
 */
export interface DiffRow {
  kind: DiffRowKind;
  /** Numéro de ligne dans la version de base (1-indexé), `null` si absente. */
  baseLineNumber: number | null;
  /** Numéro de ligne dans la version cible (1-indexé), `null` si absente. */
  targetLineNumber: number | null;
  /** Contenu de la ligne, sans le saut de ligne final. */
  content: string;
}

/** Décompte des lignes ajoutées, supprimées et inchangées. */
export interface DiffSummary {
  added: number;
  removed: number;
  unchanged: number;
}

/**
 * Deux lignes mises en regard pour la vue côte à côte. `left` est le côté base,
 * `right` le côté cible ; l'un des deux peut manquer.
 */
export interface DiffPair {
  /** `changed` lorsqu'une suppression et un ajout se font face. */
  kind: DiffRowKind | 'changed';
  left: DiffRow | null;
  right: DiffRow | null;
}

/**
 * Découpe le contenu d'un segment en lignes.
 *
 * Un texte se terminant par un saut de ligne produit un dernier élément vide
 * lors du `split` : ce n'est pas une ligne, c'est la fin de la précédente. On
 * la retire, sans quoi tout fichier bien formé compterait une ligne de trop.
 * Un texte vide ne compte, lui, aucune ligne.
 */
export function splitPartLines(value: string): string[] {
  if (value === '') return [];
  const lines = value.split('\n');
  if (lines[lines.length - 1] === '') lines.pop();
  return lines;
}

/**
 * Construit le comparatif ligne à ligne entre deux contenus.
 *
 * Les numéros de ligne sont incrémentés indépendamment de chaque côté : une
 * ligne ajoutée fait avancer la cible seule, une ligne supprimée la base seule,
 * une ligne inchangée les deux. C'est exactement ce qui rend la numérotation
 * juste après une insertion.
 */
export function buildDiffRows(baseCode: string, targetCode: string): DiffRow[] {
  const rows: DiffRow[] = [];
  let baseLineNumber = 0;
  let targetLineNumber = 0;

  for (const part of diffLines(baseCode, targetCode)) {
    for (const content of splitPartLines(part.value)) {
      if (part.added) {
        targetLineNumber += 1;
        rows.push({ kind: 'added', baseLineNumber: null, targetLineNumber, content });
      } else if (part.removed) {
        baseLineNumber += 1;
        rows.push({ kind: 'removed', baseLineNumber, targetLineNumber: null, content });
      } else {
        baseLineNumber += 1;
        targetLineNumber += 1;
        rows.push({ kind: 'unchanged', baseLineNumber, targetLineNumber, content });
      }
    }
  }

  return rows;
}

/** Décompte des lignes par nature. */
export function summarizeDiff(rows: readonly DiffRow[]): DiffSummary {
  const summary: DiffSummary = { added: 0, removed: 0, unchanged: 0 };
  for (const row of rows) {
    if (row.kind === 'added') summary.added += 1;
    else if (row.kind === 'removed') summary.removed += 1;
    else summary.unchanged += 1;
  }
  return summary;
}

/** Vrai lorsque rien n'a été ajouté ni supprimé : les deux contenus coïncident. */
export function isIdenticalSummary(summary: DiffSummary): boolean {
  return summary.added === 0 && summary.removed === 0;
}

/** Marque du pluriel français, appliquée au nom comme au participe. */
function plural(count: number): string {
  return count > 1 ? 's' : '';
}

/** Résumé en français : « 12 lignes ajoutées, 3 supprimées ». */
export function formatDiffSummary(summary: DiffSummary): string {
  const added = `${summary.added} ligne${plural(summary.added)} ajoutée${plural(summary.added)}`;
  const removed = `${summary.removed} ligne${plural(summary.removed)} supprimée${plural(
    summary.removed,
  )}`;

  // Le second membre ne répète pas le mot « ligne » : « 12 lignes ajoutées,
  // 3 supprimées » se lit mieux que la répétition complète.
  if (summary.added > 0 && summary.removed > 0) {
    return `${added}, ${summary.removed} supprimée${plural(summary.removed)}`;
  }
  if (summary.added > 0) return added;
  if (summary.removed > 0) return removed;
  return 'Aucune ligne modifiée';
}

/**
 * Aligne les lignes pour la vue côte à côte.
 *
 * Les suppressions et les ajouts consécutifs sont accumulés puis appariés deux
 * à deux : une ligne réécrite apparaît ainsi sur une seule rangée, ancienne
 * version à gauche et nouvelle à droite. Le surplus d'un côté occupe une rangée
 * dont l'autre moitié reste vide.
 */
export function pairDiffRows(rows: readonly DiffRow[]): DiffPair[] {
  const pairs: DiffPair[] = [];
  let removedBuffer: DiffRow[] = [];
  let addedBuffer: DiffRow[] = [];

  /** Vide les tampons en appariant ce qui peut l'être. */
  const flush = (): void => {
    const count = Math.max(removedBuffer.length, addedBuffer.length);
    for (let index = 0; index < count; index += 1) {
      const left = removedBuffer[index] ?? null;
      const right = addedBuffer[index] ?? null;
      pairs.push({
        kind: left && right ? 'changed' : left ? 'removed' : 'added',
        left,
        right,
      });
    }
    removedBuffer = [];
    addedBuffer = [];
  };

  for (const row of rows) {
    if (row.kind === 'removed') {
      removedBuffer.push(row);
    } else if (row.kind === 'added') {
      addedBuffer.push(row);
    } else {
      flush();
      pairs.push({ kind: 'unchanged', left: row, right: row });
    }
  }
  flush();

  return pairs;
}

/** Ce dont le sélecteur de comparaison a besoin : un identifiant, rien de plus. */
export interface ComparableVersion {
  id: string;
}

/**
 * Version proposée par défaut comme terme de comparaison (§3.5 : « par défaut,
 * la version sélectionnée et celle qui la précède »).
 *
 * `versions` est ordonnée du plus récent au plus ancien, comme le renvoie
 * `GET /api/apps/:id/versions` : la version précédente est donc la suivante
 * dans le tableau. Pour la toute première version, il n'y a rien avant elle :
 * on la compare à elle-même, ce que l'API traduit par `identical: true` plutôt
 * que par une erreur.
 */
export function defaultComparisonVersionId(
  versions: readonly ComparableVersion[],
  selectedId: string | null,
): string | null {
  if (selectedId === null) return null;
  const index = versions.findIndex((version) => version.id === selectedId);
  if (index === -1) return null;
  return versions[index + 1]?.id ?? selectedId;
}
